"use client";

import { Rating, useToast } from "@atlas/core";
import { FilterBuilder, useSessionData, useTranslation } from "@atlas/sdk";
import {
  useCreateHubRating,
  useSearchHubRatings,
  useUpdateHubRating,
} from "@query";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IPostCatalogRatingProps {
  catalogId: number;
  catalogVersion: number;
}

export function PostCatalogRating({
  catalogId,
  catalogVersion,
}: IPostCatalogRatingProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [userRating, setUserRating] = useState<number>(0);

  const { userId } = useSessionData();

  const { data: ratings, isLoading: isLoadingRatings } = useSearchHubRatings(
    {
      filter: new FilterBuilder()
        .equals("catalogId", catalogId)

        .equals("ratedBy", userId ?? ""),
    },
    {
      enabled: userId != null,
    },
  );
  useEffect(() => {
    const firstItem = ratings?.pages?.[0]?.items?.[0];
    if (firstItem?.rating != null) {
      setUserRating(firstItem.rating);
    } else {
      setUserRating(0);
    }
  }, [ratings, userId]);
  const userRatingId = useMemo(
    () => ratings?.pages?.[0]?.items?.[0]?.ratingId ?? 0,
    [ratings],
  );
  const { mutateAsync: postRating, isPending: isPostingRating } =
    useCreateHubRating();
  const { mutateAsync: updateRating, isPending: isUpdatingRating } =
    useUpdateHubRating();

  const isBusy = useMemo(
    () => isPostingRating || isUpdatingRating || isLoadingRatings,
    [isPostingRating, isUpdatingRating, isLoadingRatings],
  );

  const handleRatingChange = useCallback(
    async (rating: number) => {
      try {
        if (userRatingId == null || userRatingId === 0) {
          await postRating({
            catalogId,
            version: catalogVersion,
            rating,
            ratedBy: userId ?? "",
            ratedDatetime: new Date().toISOString(),
          });
        } else {
          await updateRating({
            ratingId: userRatingId,
            catalogId,
            version: catalogVersion,
            rating,
            ratedBy: userId ?? "",
            ratedDatetime: new Date().toISOString(),
          });
        }
        toast.add({
          title: t("hub.catalog_details.rating_posted", {
            defaultValue: "Rating posted",
          }),
          data: { type: "success" },
        });
      } catch (error) {
        console.error("Failed to post/update rating", error);
        toast.add({
          title: t("hub.catalog_details.rating_post_error", {
            defaultValue: "Could not post rating",
          }),
          data: { type: "error" },
        });
      }
    },
    [
      userRatingId,
      postRating,
      updateRating,
      userId,
      catalogId,
      catalogVersion,
      toast,
      t,
    ],
  );
  return (
    <Rating
      disabled={isBusy}
      editable={!isBusy}
      rating={userRating}
      maxRating={5}
      precision={0.1}
      showValue={true}
      size="md"
      readonly={false}
      onRatingChange={(rating) => {
        handleRatingChange(rating);
      }}
    />
  );
}
